import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { getDiasFestivos, getEmpleados, getVacaciones } from "@/lib/api";
import { MESES, pad2, TIPOS, tipoColor, tipoLabel } from "@/lib/tipos";

const HOY = new Date();

function aniosOpciones(): number[] {
  const y = HOY.getFullYear();
  return [y - 2, y - 1, y, y + 1];
}

function nombreCorto(email: string, nombre: string | null): string {
  return nombre && nombre.trim() ? nombre : email.split("@")[0];
}

export function EquipoMesPage() {
  // Año/mes pueden venir por query string (deep-link desde /anio).
  const [searchParams, setSearchParams] = useSearchParams();
  const [anio, setAnio] = useState(() => Number(searchParams.get("anio")) || HOY.getFullYear());
  const [mes, setMes] = useState(() => {
    const m = Number(searchParams.get("mes"));
    return m >= 1 && m <= 12 ? m : HOY.getMonth() + 1;
  });

  // Mantén la URL en sync con los selectores (sin apilar historial).
  useEffect(() => {
    if (searchParams.get("anio") !== String(anio) || searchParams.get("mes") !== String(mes)) {
      setSearchParams({ anio: String(anio), mes: String(mes) }, { replace: true });
    }
  }, [anio, mes, searchParams, setSearchParams]);

  const empleadosQ = useQuery({ queryKey: ["empleados"], queryFn: () => getEmpleados() });
  const vacQ = useQuery({
    queryKey: ["vacaciones", "mes", anio, mes],
    queryFn: () => getVacaciones({ anio, mes }),
  });
  const festivosQ = useQuery({
    queryKey: ["dias-festivos"],
    queryFn: getDiasFestivos,
    staleTime: Infinity,
  });
  const festivosMap = useMemo(
    () => new Map((festivosQ.data ?? []).map((f) => [f.fecha, f.descripcion])),
    [festivosQ.data],
  );

  const diasEnMes = new Date(anio, mes, 0).getDate();
  const dias = useMemo(() => Array.from({ length: diasEnMes }, (_, i) => i + 1), [diasEnMes]);

  // Por empleado: día(1..n) -> tipo (el primero que aplique ese día).
  const porEmpleado = useMemo(() => {
    const map = new Map<string, Map<number, string>>();
    for (const v of vacQ.data ?? []) {
      let dm = map.get(v.empleado_email);
      if (!dm) {
        dm = new Map();
        map.set(v.empleado_email, dm);
      }
      for (const d of dias) {
        const iso = `${anio}-${pad2(mes)}-${pad2(d)}`;
        if (v.fecha_inicio <= iso && iso <= v.fecha_fin && !dm.has(d)) {
          dm.set(d, v.tipo);
        }
      }
    }
    return map;
  }, [vacQ.data, dias, anio, mes]);

  // Filas: todo el equipo activo; si aún no hay roster, deriva de las vacaciones.
  const filas = useMemo(() => {
    const base = (empleadosQ.data ?? []).map((e) => ({
      email: e.email,
      nombre: nombreCorto(e.email, e.nombre),
    }));
    if (base.length > 0) return base.sort((a, b) => a.nombre.localeCompare(b.nombre));
    const set = new Map<string, string>();
    for (const v of vacQ.data ?? []) {
      set.set(v.empleado_email, nombreCorto(v.empleado_email, v.empleado_nombre));
    }
    return [...set.entries()]
      .map(([email, nombre]) => ({ email, nombre }))
      .sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [empleadosQ.data, vacQ.data]);

  const esFinde = (d: number) => {
    const wd = new Date(anio, mes - 1, d).getDay();
    return wd === 0 || wd === 6;
  };

  const festivoDe = (d: number) => festivosMap.get(`${anio}-${pad2(mes)}-${pad2(d)}`);

  // Día de hoy (solo si se está viendo el mes en curso): línea vertical en su columna.
  const hoyD = anio === HOY.getFullYear() && mes === HOY.getMonth() + 1 ? HOY.getDate() : null;

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Equipo · por mes</h1>
          <p className="text-sm text-slate-500 mt-1">Quién está fuera cada día del mes.</p>
        </div>
        <div className="flex items-end gap-2">
          <label className="block">
            <span className="text-xs font-medium text-slate-600">Mes</span>
            <select
              className="mt-1 border rounded px-2 py-1.5 text-sm block"
              value={mes}
              onChange={(e) => setMes(Number(e.target.value))}
            >
              {MESES.map((m, i) => (
                <option key={i} value={i + 1}>
                  {m}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-medium text-slate-600">Año</span>
            <select
              className="mt-1 border rounded px-2 py-1.5 text-sm block"
              value={anio}
              onChange={(e) => setAnio(Number(e.target.value))}
            >
              {aniosOpciones().map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 text-xs text-slate-600">
        {TIPOS.map((t) => (
          <span key={t.value} className="inline-flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: t.color }} />
            {t.label}
          </span>
        ))}
        {hoyD != null && (
          <span className="inline-flex items-center gap-1.5">
            <span className="w-0.5 h-3 rounded-sm" style={{ backgroundColor: "#384d3a" }} />
            Hoy
          </span>
        )}
      </div>

      {vacQ.isError && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">
          Error: {(vacQ.error as Error).message}
        </div>
      )}

      <div className="bg-white border rounded-lg overflow-x-auto">
        <table className="text-xs border-collapse">
          <thead>
            <tr className="text-slate-500">
              <th className="sticky left-0 z-10 bg-white text-left px-3 py-2 font-medium min-w-[12rem] border-b">
                Empleado
              </th>
              {dias.map((d) => {
                const fest = festivoDe(d);
                const esHoy = d === hoyD;
                return (
                  <th
                    key={d}
                    title={esHoy ? `Hoy${fest ? ` · ${fest}` : ""}` : fest || undefined}
                    className={`px-0 py-2 w-7 text-center font-medium border-b ${
                      fest ? "bg-amber-50 text-amber-500" : esFinde(d) ? "bg-slate-50 text-slate-400" : ""
                    } ${esHoy ? "col-hoy" : ""}`}
                  >
                    {d}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {filas.length === 0 && (
              <tr>
                <td colSpan={diasEnMes + 1} className="px-3 py-8 text-center text-slate-500">
                  Sin empleados todavía.
                </td>
              </tr>
            )}
            {filas.map((emp) => {
              const dm = porEmpleado.get(emp.email);
              return (
                <tr key={emp.email} className="border-t">
                  <td className="sticky left-0 z-10 bg-white px-3 py-1.5 whitespace-nowrap border-r">
                    {emp.nombre}
                  </td>
                  {dias.map((d) => {
                    const tipo = dm?.get(d);
                    const fest = festivoDe(d);
                    const bgCls = tipo
                      ? ""
                      : fest
                        ? "bg-amber-50"
                        : esFinde(d)
                          ? "bg-slate-50"
                          : "";
                    return (
                      <td
                        key={d}
                        className={`w-7 h-7 text-center ${bgCls} ${d === hoyD ? "col-hoy" : ""}`}
                        title={tipo ? `${emp.nombre} · ${tipoLabel(tipo)}` : fest || undefined}
                        style={tipo ? { backgroundColor: tipoColor(tipo) } : undefined}
                      />
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
